import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { useMonthOptions } from '../lib/useMonthOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Loader2, CheckCircle2, AlertTriangle, Clock, Lock, ShieldCheck, Plus, Calendar, Filter, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

function fmt(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v); }

const STATUT_CFG = {
    a_controler: { label: 'À contrôler', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    conforme: { label: 'Conforme', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    anomalie: { label: 'Anomalie', color: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
    valide: { label: 'Validé', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Lock },
    valide_deduction: { label: 'Validé + Déduit', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: ShieldCheck },
};

export default function ControleCGPage() {
    const [data, setData] = useState([]);
    const [kpi, setKpi] = useState({});
    const [loading, setLoading] = useState(true);
    const [filterMois, setFilterMois] = useState('');
    const [filterStatut, setFilterStatut] = useState('');
    const [searchQ, setSearchQ] = useState('');
    const monthOptions = useMonthOptions();

    // Add control dialog
    const [showAdd, setShowAdd] = useState(false);
    const [addSearch, setAddSearch] = useState('');
    const [addResults, setAddResults] = useState([]);
    const [addLoading, setAddLoading] = useState(false);
    const [addMois, setAddMois] = useState(() => {
        const n = new Date();
        return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
    });

    // Validate dialog
    const [validateTarget, setValidateTarget] = useState(null);
    const [validateComment, setValidateComment] = useState('');
    const [validateMoisDed, setValidateMoisDed] = useState('');

    // Inline edit tracking
    const [editingId, setEditingId] = useState(null);
    const [editAttendu, setEditAttendu] = useState('');
    const [editReel, setEditReel] = useState('');
    const attenduRef = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterMois) params.set('mois', filterMois);
            if (filterStatut) params.set('statut', filterStatut);
            if (searchQ) params.set('q', searchQ);
            const [listRes, kpiRes] = await Promise.all([
                axios.get(`${API_URL}/api/controles-cg?${params}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/controles-cg/kpi${filterMois ? `?mois=${filterMois}` : ''}`, { withCredentials: true }),
            ]);
            setData(listRes.data);
            setKpi(kpiRes.data);
        } catch { toast.error('Erreur chargement'); }
        setLoading(false);
    }, [filterMois, filterStatut, searchQ]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Search vehicles for adding
    const searchVehicles = async (q) => {
        setAddSearch(q);
        if (q.length < 2) { setAddResults([]); return; }
        setAddLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/controles-cg/search-vehicle?immatriculation=${encodeURIComponent(q)}`, {}, { withCredentials: true });
            setAddResults(res.data);
        } catch { setAddResults([]); }
        setAddLoading(false);
    };

    const handleAddControl = async (vehicle) => {
        try {
            await axios.post(`${API_URL}/api/controles-cg`, {
                vehicle_id: vehicle.id,
                mois: addMois,
                montant_attendu: 0,
            }, { withCredentials: true });
            toast.success(`Contrôle CG créé — ${vehicle.immatriculation}`);
            setShowAdd(false);
            setAddSearch('');
            setAddResults([]);
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    // Inline save
    const handleInlineSave = async (item) => {
        const attendu = parseFloat(editAttendu);
        const reel = editReel !== '' ? parseFloat(editReel) : null;
        try {
            const payload = {};
            if (!isNaN(attendu)) payload.montant_attendu = attendu;
            if (reel !== null && !isNaN(reel)) payload.montant_reel_paye = reel;
            await axios.put(`${API_URL}/api/controles-cg/${item.id}`, payload, { withCredentials: true });
            toast.success('Mis à jour');
            setEditingId(null);
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditAttendu(item.montant_attendu || '');
        setEditReel(item.montant_reel_paye != null ? item.montant_reel_paye : '');
        setTimeout(() => attenduRef.current?.focus(), 50);
    };

    // Validate actions
    const handleValidate = async (action) => {
        if (!validateTarget) return;
        try {
            await axios.post(`${API_URL}/api/controles-cg/${validateTarget.id}/validate`, {
                action,
                commentaire: validateComment,
                mois_deduction: validateMoisDed || validateTarget.mois,
            }, { withCredentials: true });
            toast.success(action === 'valider_deduire' ? 'Validé + déduction créée' : 'Validé');
            setValidateTarget(null);
            setValidateComment('');
            fetchData();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="controle-cg-page">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8">
                <h1 className="text-xl font-semibold text-gray-900 tracking-tight">Contrôle Cartes Grises</h1>
                <Button onClick={() => setShowAdd(true)} className="bg-[#6366F1] hover:bg-[#4F46E5] text-white text-[13px] h-9 rounded-lg px-4 shadow-sm" data-testid="add-cg-btn">
                    <Plus className="w-4 h-4 mr-1.5" />Nouveau contrôle
                </Button>
            </div>

            {/* KPI */}
            <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6" data-testid="cg-kpi">
                <KPI label="Total" value={kpi.total || 0} />
                <KPI label="Contrôlés" value={kpi.controlees || 0} />
                <KPI label="Conformes" value={kpi.conformes || 0} color="text-emerald-600" />
                <KPI label="Anomalies" value={kpi.anomalies || 0} color="text-red-600" />
                <KPI label="À contrôler" value={kpi.a_controler || 0} color="text-amber-600" />
                <KPI label="Écarts +" value={fmt(kpi.ecarts_positifs || 0)} color="text-red-600" small />
                <KPI label="Déduits" value={fmt(kpi.deductions_validees || 0)} color="text-indigo-600" small />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <Select value={filterMois || 'all'} onValueChange={v => setFilterMois(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-44 bg-white border-gray-200 text-[13px] h-9 rounded-lg shadow-sm" data-testid="cg-filter-mois">
                        <Calendar className="w-3.5 h-3.5 mr-2 text-gray-400" /><SelectValue />
                    </SelectTrigger>
                    <SelectContent><SelectItem value="all">Tous les mois</SelectItem>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterStatut || 'all'} onValueChange={v => setFilterStatut(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-36 bg-white border-gray-200 text-[13px] h-9 rounded-lg shadow-sm" data-testid="cg-filter-statut">
                        <Filter className="w-3.5 h-3.5 mr-2 text-gray-400" /><SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous statuts</SelectItem>
                        <SelectItem value="a_controler">À contrôler</SelectItem>
                        <SelectItem value="conforme">Conforme</SelectItem>
                        <SelectItem value="anomalie">Anomalie</SelectItem>
                        <SelectItem value="valide">Validé</SelectItem>
                        <SelectItem value="valide_deduction">Validé + Déduit</SelectItem>
                    </SelectContent>
                </Select>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <Input placeholder="Recherche plaque / véhicule..." value={searchQ} onChange={e => setSearchQ(e.target.value)} className="pl-9 w-52 bg-white border-gray-200 text-[13px] h-9" data-testid="cg-search" />
                </div>
            </div>

            {/* Table */}
            {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div> : (
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]" data-testid="cg-table">
                            <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                                <th className="text-left py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Plaque</th>
                                <th className="text-left py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Véhicule</th>
                                <th className="text-left py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Commercial</th>
                                <th className="text-right py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">CG attendu</th>
                                <th className="text-right py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Virement réel</th>
                                <th className="text-right py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Écart</th>
                                <th className="text-center py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Statut</th>
                                <th className="text-right py-3 px-3 text-[11px] text-gray-400 uppercase tracking-wider font-medium">Actions</th>
                            </tr></thead>
                            <tbody>
                                {data.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-gray-400">Aucun contrôle CG enregistré</td></tr>}
                                {data.map(item => {
                                    const st = STATUT_CFG[item.statut] || STATUT_CFG.a_controler;
                                    const Icon = st.icon;
                                    const isEditing = editingId === item.id;
                                    const locked = item.statut === 'valide' || item.statut === 'valide_deduction';
                                    return (
                                        <tr key={item.id} className={`border-b border-gray-50 transition-colors ${isEditing ? 'bg-indigo-50/30' : 'hover:bg-gray-50/50'}`} data-testid={`cg-row-${item.id}`}>
                                            <td className="py-2.5 px-3 font-mono font-semibold text-gray-900 text-[12px]">{item.immatriculation}</td>
                                            <td className="py-2.5 px-3 text-gray-700 truncate max-w-[140px]">{item.vehicule_nom || item.vehicule_modele}</td>
                                            <td className="py-2.5 px-3 text-gray-500">{item.commercial_name || '—'}</td>
                                            <td className="py-2.5 px-3 text-right">
                                                {isEditing ? (
                                                    <Input ref={attenduRef} type="number" step="0.01" value={editAttendu} onChange={e => setEditAttendu(e.target.value)}
                                                        className="w-24 h-7 text-right text-[12px] font-mono ml-auto" data-testid="cg-edit-attendu"
                                                        onKeyDown={e => { if (e.key === 'Tab') { e.preventDefault(); document.querySelector('[data-testid="cg-edit-reel"]')?.focus(); } }} />
                                                ) : (
                                                    <span className="font-mono text-gray-900 cursor-pointer hover:text-indigo-600" onClick={() => !locked && startEdit(item)}>
                                                        {item.montant_attendu ? fmt(item.montant_attendu) : <span className="text-gray-300">Saisir</span>}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-3 text-right">
                                                {isEditing ? (
                                                    <Input type="number" step="0.01" value={editReel} onChange={e => setEditReel(e.target.value)}
                                                        className="w-24 h-7 text-right text-[12px] font-mono ml-auto" data-testid="cg-edit-reel"
                                                        onKeyDown={e => { if (e.key === 'Enter') handleInlineSave(item); }}
                                                        placeholder="—" />
                                                ) : (
                                                    <span className="font-mono text-gray-900 cursor-pointer hover:text-indigo-600" onClick={() => !locked && startEdit(item)}>
                                                        {item.montant_reel_paye != null ? fmt(item.montant_reel_paye) : <span className="text-gray-300">Saisir</span>}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-3 text-right">
                                                {item.ecart != null ? (
                                                    <span className={`font-mono font-medium ${item.ecart === 0 ? 'text-emerald-600' : item.ecart > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                                        {item.ecart > 0 ? '+' : ''}{fmt(item.ecart)}
                                                    </span>
                                                ) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${st.color}`}>
                                                    <Icon className="w-3 h-3" />{st.label}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {isEditing && (
                                                        <>
                                                            <Button size="sm" onClick={() => handleInlineSave(item)} className="h-6 px-2 text-[11px] bg-[#6366F1] text-white" data-testid="cg-save-btn">OK</Button>
                                                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-6 px-2 text-[11px]">Annuler</Button>
                                                        </>
                                                    )}
                                                    {!isEditing && item.statut === 'anomalie' && (
                                                        <Button size="sm" variant="outline" onClick={() => { setValidateTarget(item); setValidateComment(''); setValidateMoisDed(item.mois); }}
                                                            className="h-6 px-2 text-[11px] border-red-200 text-red-600 hover:bg-red-50" data-testid="cg-validate-btn">
                                                            Traiter
                                                        </Button>
                                                    )}
                                                    {!isEditing && item.statut === 'conforme' && (
                                                        <Button size="sm" variant="outline" onClick={async () => {
                                                            try { await axios.post(`${API_URL}/api/controles-cg/${item.id}/validate`, { action: 'valider' }, { withCredentials: true }); toast.success('Validé'); fetchData(); }
                                                            catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                                                        }} className="h-6 px-2 text-[11px] border-emerald-200 text-emerald-600 hover:bg-emerald-50" data-testid="cg-confirm-btn">
                                                            Valider
                                                        </Button>
                                                    )}
                                                    {item.deduction_id && (
                                                        <span className="text-[10px] text-indigo-500 font-medium" title="Déduction créée">Déduit</span>
                                                    )}
                                                    {item.commentaire && (
                                                        <MessageSquare className="w-3.5 h-3.5 text-gray-300" title={item.commentaire} />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Add Control Dialog */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Nouveau contrôle CG</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1.5 block">Mois de contrôle</label>
                            <Select value={addMois} onValueChange={setAddMois}>
                                <SelectTrigger className="h-9 text-sm" data-testid="cg-add-mois"><SelectValue /></SelectTrigger>
                                <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-1.5 block">Recherche par plaque</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <Input placeholder="AB-123-CD..." value={addSearch} onChange={e => searchVehicles(e.target.value)} className="pl-9 text-sm font-mono" autoFocus data-testid="cg-add-search" />
                            </div>
                        </div>
                        {addLoading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
                        <div className="max-h-56 overflow-y-auto space-y-1">
                            {addResults.map(v => (
                                <button key={v.id} onClick={() => handleAddControl(v)} className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[#6366F1]/5 border border-gray-100 transition-colors" data-testid={`cg-add-vehicle-${v.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-[13px] font-medium text-gray-900">{v.immatriculation} — {v.nom || v.modele}</p>
                                            <p className="text-[11px] text-gray-400">{v.commercial || 'Aucun commercial'} {v.date_vente ? `· Vendu ${v.date_vente}` : ''}</p>
                                        </div>
                                        <Plus className="w-4 h-4 text-[#6366F1] shrink-0" />
                                    </div>
                                </button>
                            ))}
                            {!addLoading && addResults.length === 0 && addSearch.length >= 2 && <p className="text-center text-[13px] text-gray-400 py-4">Aucun véhicule trouvé</p>}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Validate/Deduct Dialog */}
            <AlertDialog open={!!validateTarget} onOpenChange={v => { if (!v) setValidateTarget(null); }}>
                <AlertDialogContent className="max-w-lg" data-testid="cg-validate-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Anomalie CG — {validateTarget?.immatriculation}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3">
                                <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase">Attendu</p>
                                        <p className="font-mono font-medium">{validateTarget && fmt(validateTarget.montant_attendu)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase">Viré</p>
                                        <p className="font-mono font-medium">{validateTarget && fmt(validateTarget.montant_reel_paye)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase">Écart</p>
                                        <p className={`font-mono font-semibold ${(validateTarget?.ecart || 0) > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                            {validateTarget && ((validateTarget.ecart > 0 ? '+' : '') + fmt(validateTarget.ecart))}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-400 uppercase font-medium block mb-1">Commentaire / justification</label>
                                    <Input value={validateComment} onChange={e => setValidateComment(e.target.value)} placeholder="Raison de l'écart..." data-testid="cg-validate-comment" />
                                </div>
                                {(validateTarget?.ecart || 0) > 0 && (
                                    <div>
                                        <label className="text-xs text-gray-400 uppercase font-medium block mb-1">Mois de déduction (si déduction)</label>
                                        <Select value={validateMoisDed} onValueChange={setValidateMoisDed}>
                                            <SelectTrigger className="h-9 text-sm" data-testid="cg-validate-mois"><SelectValue /></SelectTrigger>
                                            <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel data-testid="cg-validate-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleValidate('valider')} className="bg-gray-600 hover:bg-gray-700 text-white" data-testid="cg-validate-sans-deduction">
                            Valider sans déduction
                        </AlertDialogAction>
                        {(validateTarget?.ecart || 0) > 0 && (
                            <AlertDialogAction onClick={() => handleValidate('valider_deduire')} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cg-validate-deduire">
                                Déduire {fmt(validateTarget?.ecart || 0)}
                            </AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function KPI({ label, value, color = 'text-gray-900', small = false }) {
    return (
        <div className="card p-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-medium mb-1">{label}</p>
            <p className={`${small ? 'text-base' : 'text-lg'} font-semibold ${color}`} style={{ letterSpacing: '-0.02em' }}>{value}</p>
        </div>
    );
}
